import { useState } from "react";
import type { SupportContext } from "@b2bportal/rbc-hts-assist-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { HtsAssistLobbyEntry } from "./HtsAssistLobbyEntry";
import { HtsAssistLobbyTripCard } from "./HtsAssistLobbyTripCard";
import {
  getHtsAssistLobbySupportContext,
  type HtsAssistLobbyCategory,
} from "./htsAssistLobbyTrips";
import { useHtsAssistLobbyTrips } from "./useHtsAssistLobbyTrips";

interface HtsAssistLobbyProps {
  onStartQuery: (query: string) => void;
  onSelectBooking: (supportContext: SupportContext) => void;
}

const CATEGORIES: HtsAssistLobbyCategory[] = ["upcoming", "past", "cancelled"];

export const HtsAssistLobby = ({
  onStartQuery,
  onSelectBooking,
}: HtsAssistLobbyProps) => {
  const { t } = useI18nContext();
  const [selectedCategory, setSelectedCategory] =
    useState<HtsAssistLobbyCategory>("upcoming");
  const { categories, hasError, isLoading, retry } = useHtsAssistLobbyTrips();
  const availableCategories = CATEGORIES.filter(
    (category) => categories[category].length > 0
  );
  const activeCategory = availableCategories.includes(selectedCategory)
    ? selectedCategory
    : (availableCategories[0] ?? "upcoming");
  const showCategoryTabs =
    categories.past.length > 0 || categories.cancelled.length > 0;

  const getCategoryLabel = (category: HtsAssistLobbyCategory) => {
    const count = categories[category].length;
    switch (category) {
      case "upcoming":
        return t("chat.htsAssistLobbyCategory.upcoming", { count });
      case "past":
        return t("chat.htsAssistLobbyCategory.past", { count });
      case "cancelled":
        return t("chat.htsAssistLobbyCategory.cancelled", { count });
    }
  };

  const getEmptyMessage = (category: HtsAssistLobbyCategory) => {
    switch (category) {
      case "upcoming":
        return t("chat.htsAssistLobbyEmpty.upcoming");
      case "past":
        return t("chat.htsAssistLobbyEmpty.past");
      case "cancelled":
        return t("chat.htsAssistLobbyEmpty.cancelled");
    }
  };

  return (
    <main className="hts-assist-lobby">
      <HtsAssistLobbyEntry onStartQuery={onStartQuery} variant="embedded" />

      <section
        aria-label={t("chat.htsAssistLobbyTrips")}
        className="hts-assist-lobby__trips"
      >
        {showCategoryTabs ? (
          <div
            aria-label={t("chat.htsAssistLobbyTripCategories")}
            className="hts-assist-lobby__tabs"
            role="tablist"
          >
            {availableCategories.map((category) => (
              <button
                aria-controls={`hts-assist-lobby-${category}`}
                aria-selected={activeCategory === category}
                className="hts-assist-lobby__tab"
                id={`hts-assist-lobby-${category}-tab`}
                key={category}
                onClick={() => setSelectedCategory(category)}
                role="tab"
                type="button"
              >
                {getCategoryLabel(category)}
              </button>
            ))}
          </div>
        ) : null}

        <div
          aria-labelledby={
            showCategoryTabs
              ? `hts-assist-lobby-${activeCategory}-tab`
              : undefined
          }
          className="hts-assist-lobby__trip-list"
          id={`hts-assist-lobby-${activeCategory}`}
          role={showCategoryTabs ? "tabpanel" : undefined}
        >
          {isLoading ? (
            <div
              aria-label={t("chat.htsAssistLobbyTripsLoading")}
              className="hts-assist-lobby__loading"
              role="status"
            >
              {[0, 1].map((item) => (
                <span
                  aria-hidden="true"
                  className="hts-assist-lobby__skeleton"
                  key={item}
                />
              ))}
            </div>
          ) : hasError ? (
            <div className="hts-assist-lobby__state" role="alert">
              <p>{t("chat.htsAssistLobbyTripsError")}</p>
              <button onClick={retry} type="button">
                {t("chat.htsAssistLobbyRetry")}
              </button>
            </div>
          ) : categories[activeCategory].length ? (
            categories[activeCategory].map((trip) => (
              <HtsAssistLobbyTripCard
                key={`${trip.productType}-${trip.reservationId}`}
                onSelect={(selectedTrip) =>
                  onSelectBooking(getHtsAssistLobbySupportContext(selectedTrip))
                }
                trip={trip}
              />
            ))
          ) : (
            <p className="hts-assist-lobby__state">
              {getEmptyMessage(activeCategory)}
            </p>
          )}
        </div>
      </section>
    </main>
  );
};
